import { useState, useEffect } from "react";
import { FormikProps } from "formik";
import { LocationFormValues } from "../types";
import { StoreState } from "enum/StoreState";

export type LocationType = "point" | "area";

interface UseLocationTypeSelectionProps {
  isEditing: boolean;
  isOpen: boolean;
  operationRuleLocation: any;
  formik: FormikProps<LocationFormValues>;
  oceanWeatherIntelligenceStore: any;
}

export function useLocationTypeSelection({
  isEditing,
  isOpen,
  operationRuleLocation,
  formik,
  oceanWeatherIntelligenceStore,
}: UseLocationTypeSelectionProps) {
  const [locationType, setLocationType] = useState<LocationType | null>(null);
  const [userClearedData, setUserClearedData] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setLocationType(null);
      setUserClearedData(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      isEditing &&
      operationRuleLocation &&
      !formik.values.locationId &&
      (!formik.values.geofence || formik.values.geofence.length === 0) &&
      !formik.values.stationRegion
    ) {
      setUserClearedData(true);
      return;
    }

    if (operationRuleLocation && isEditing && !userClearedData) {
      if (operationRuleLocation.locationId && operationRuleLocation.locationId > 0) {
        setLocationType("area");
        // Only set locationId if it hasn't been manually changed by the user
        if (!formik.values.locationId) {
          formik.setFieldValue("locationId", operationRuleLocation.locationId);
        }
      }
      else if (operationRuleLocation.geofence && operationRuleLocation.geofence.length > 0) {
        setLocationType("area");
        // Only set geofence if it hasn't been manually changed by the user
        if (!formik.values.geofence || formik.values.geofence.length === 0) {
          formik.setFieldValue("geofence", operationRuleLocation.geofence);
        }
      }
      else if (operationRuleLocation.stationId && operationRuleLocation.regionId) {
        const { stationId, regionId, macroRegionId } = operationRuleLocation;

        setLocationType("point");

        if (macroRegionId && macroRegionId > 0) {
          oceanWeatherIntelligenceStore
            .getStationForLocationAPI(macroRegionId)
            .then(() => {
              formik.setFieldValue("stationRegion", `${stationId}-${regionId}`);
              formik.setFieldValue("macroRegionId", macroRegionId);
            });
        }
      }
    } else {
      if (locationType === null) {
        const hasGeofence = formik.values.geofence && formik.values.geofence.length > 0;
        setLocationType(hasGeofence ? "area" : null);
      }
      oceanWeatherIntelligenceStore.setState(StoreState.DONE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationRuleLocation, isEditing, isOpen, formik.values.geofence, userClearedData]);

  const handleBack = () => {
    setUserClearedData(true);
    setLocationType(null);
  };

  const handleLocationTypeSelect = (type: LocationType) => {
    setLocationType(type);
    setUserClearedData(false);

    if (type === "area") {
      formik.setFieldValue("stationRegion", "");
      formik.setFieldValue("regionId", undefined);
    } else if (type === "point") {
      formik.setFieldValue("geofence", undefined);
      formik.setFieldValue("locationId", undefined);
    }
  };

  return {
    locationType,
    handleBack,
    handleLocationTypeSelect,
  };
}
